"use client";

import { AlertCircle, RotateCw, Home, MapPinned } from "lucide-react";
import Link from "next/link";
import * as React from "react";
import { cn } from "@/lib/utils";

// Componente unificado para boundaries (`error.tsx`, `not-found.tsx`) y estados
// de error full-panel en server components. Centraliza el look para que un
// crash, un 404 o un fallo de carga se vean iguales.
//
// Variantes:
//   - "error": rojo, icono AlertCircle (default para boundaries)
//   - "not_found": neutro, icono MapPinned (para 404)
//
// Acciones:
//   - reset: callback que dispara reload del segmento (lo pasa error.tsx).
//   - homeHref: href al dashboard del usuario (string opcional).

export interface ErrorStateProps {
  variant?: "error" | "not_found";
  title?: string;
  description?: string;
  reportId?: string | null;
  onReset?: () => void;
  homeHref?: string | null;
  className?: string;
}

const VARIANT_CONFIG = {
  error: {
    Icon: AlertCircle,
    accent: "text-[var(--color-danger)]",
    bg: "bg-[var(--color-danger-bg)]/40 border-[var(--color-danger)]/30",
    defaultTitle: "Algo no funcionó",
    defaultDescription:
      "Tuvimos un problema mostrando esta página. Probá reintentar; si vuelve a fallar, avisanos.",
  },
  not_found: {
    Icon: MapPinned,
    accent: "text-[var(--color-fg-muted)]",
    bg: "bg-[var(--color-bg-elevated)] border-[var(--color-border)]",
    defaultTitle: "No encontramos lo que buscabas",
    defaultDescription:
      "Puede que el recurso haya sido eliminado o que el link esté mal.",
  },
} as const;

export function ErrorState({
  variant = "error",
  title,
  description,
  reportId,
  onReset,
  homeHref,
  className,
}: ErrorStateProps) {
  const config = VARIANT_CONFIG[variant];
  const Icon = config.Icon;

  return (
    <div
      role="alert"
      className={cn(
        "mx-auto flex max-w-md flex-col items-center gap-4 rounded-lg border p-8 text-center",
        config.bg,
        className,
      )}
    >
      <Icon className={cn("h-10 w-10", config.accent)} aria-hidden="true" />
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">{title ?? config.defaultTitle}</h2>
        <p className="text-sm text-[var(--color-fg-muted)]">
          {description ?? config.defaultDescription}
        </p>
      </div>

      {reportId ? (
        <p className="font-mono text-[11px] text-[var(--color-fg-muted)]">
          Referencia: {reportId}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
        {onReset ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1.5 text-[13px] font-medium hover:bg-[var(--color-bg-muted)]"
          >
            <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
            Reintentar
          </button>
        ) : null}
        {homeHref ? (
          <Link
            href={homeHref}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1.5 text-[13px] font-medium hover:bg-[var(--color-bg-muted)]"
          >
            <Home className="h-3.5 w-3.5" aria-hidden="true" />
            Volver al inicio
          </Link>
        ) : null}
      </div>
    </div>
  );
}
